import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Save, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { api, formatErr } from "@/lib/api";
import { AdminHeader } from "@/components/platform/AdminHeader";

const P = "/asv/admin/settings/email";
const F = ({ label, hint, children }) => <div className="space-y-1.5"><Label className="text-xs text-slate-400">{label}</Label>{children}{hint && <p className="text-xs text-slate-500">{hint}</p>}</div>;

function Sender({ d, onSaved, onTest, busy }) {
  const [f, setF] = useState({ from_name: d.from_name, reply_to: d.reply_to });
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); try { onSaved((await api.put(P, f)).data); toast.success("Sender details saved"); } catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); } };
  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_340px]">
      <div className="card-surface space-y-4 p-6">
        <F label="Sender name" hint="Shown as the 'From' name in inboxes. Leave blank to use your website name."><Input value={f.from_name} onChange={(e) => setF({ ...f, from_name: e.target.value })} placeholder="All Sports Venues" data-testid="email-from-name" /></F>
        <F label="Reply-to address" hint="Customer replies go to this inbox. Leave blank to use the email in Settings → Site."><Input value={f.reply_to} onChange={(e) => setF({ ...f, reply_to: e.target.value })} placeholder={d.in_use.admin_email} data-testid="email-reply-to" /></F>
        <div className="flex flex-wrap gap-2">
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="email-sender-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" />Save</>}</Button>
          <Button variant="outline" className="rounded-full" onClick={onTest} disabled={busy || !d.connected} data-testid="email-test">{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Send className="mr-1 h-4 w-4" />}Send test email</Button>
        </div>
      </div>
      <div className="card-surface space-y-3 p-6 text-sm" data-testid="email-in-use">
        <p className="flex items-center gap-2 font-semibold text-white"><Mail className="h-4 w-4 text-emerald-400" />Currently in use</p>
        <p className="break-words text-slate-300">From: <b className="text-white" data-testid="email-in-use-from">{d.in_use.from}</b></p>
        <p className="text-xs text-slate-500">{d.connected ? "Sending through your Resend account. The From address is set in Settings → Resend." : "Not sending: add your Resend key in Settings → Resend."}</p>
        <p className="break-words text-slate-300">Reply-to: <b className="text-white" data-testid="email-in-use-reply">{d.in_use.reply_to || "-"}</b></p>
        <p className="text-xs text-slate-500">Test emails go to your own address. Admin copies go to {d.in_use.admin_email || "the email in Settings → Site"}.</p>
      </div>
    </div>
  );
}

function Template({ t, onSaved }) {
  const [f, setF] = useState(t);
  const [saving, setSaving] = useState(false);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const custom = (on) => setF({ ...f, custom: on, subject: f.subject || t.defaults.subject, heading: f.heading || t.defaults.heading, body: f.body || t.defaults.body });
  const save = async () => {
    setSaving(true);
    try { onSaved((await api.put(`${P}/templates/${t.key}`, { custom: f.custom, copy_admin: f.copy_admin, subject: f.subject, heading: f.heading, body: f.body })).data); toast.success(`${t.label} email saved`); }
    catch (e) { toast.error(formatErr(e)); } finally { setSaving(false); }
  };
  return (
    <div className="space-y-4" data-testid={`email-tpl-${t.key}`}>
      <label className="flex items-center gap-3 text-sm text-white"><Switch checked={f.copy_admin} onCheckedChange={set("copy_admin")} data-testid="email-tpl-copy-admin" />Send a copy to admin (email in Settings → Site)</label>
      <label className="flex items-center gap-3 text-sm text-white"><Switch checked={f.custom} onCheckedChange={custom} data-testid="email-tpl-custom" />Use custom content for this email</label>
      {f.custom ? <>
        <F label="Email subject"><Input value={f.subject} onChange={(e) => set("subject")(e.target.value)} data-testid="email-tpl-subject" /></F>
        <F label="Email heading"><Input value={f.heading} onChange={(e) => set("heading")(e.target.value)} data-testid="email-tpl-heading" /></F>
        <F label="Email message (one paragraph per line)" hint={`You can use: ${t.vars.map((v) => `{${v}}`).join(" ")}`}>
          <Textarea rows={8} value={f.body} onChange={(e) => set("body")(e.target.value)} data-testid="email-tpl-body" /></F>
        <button type="button" className="text-xs text-slate-400 underline" onClick={() => setF({ ...f, ...t.defaults })} data-testid="email-tpl-reset">Reset to default text</button>
      </> : <div className="rounded-xl border border-slate-800 p-4 text-sm text-slate-400" data-testid="email-tpl-default"><p className="font-semibold text-slate-300">{t.defaults.subject}</p>
        <p className="mt-1 whitespace-pre-line text-xs">{t.defaults.body}</p><p className="mt-2 text-xs text-slate-500">The standard text is used. Turn on custom content to change it.</p></div>}
      <Button className="rounded-full" onClick={save} disabled={saving} data-testid="email-tpl-save">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="mr-1 h-4 w-4" />Save {t.label.toLowerCase()} email</>}</Button>
    </div>
  );
}

function Group({ name, list, onSaved }) {
  const [k, setK] = useState(list[0].key);
  const t = list.find((x) => x.key === k);
  return (
    <div className="card-surface space-y-4 p-6" data-testid={`email-group-${list[0].key}`}>
      <p className="font-semibold text-white">{name}</p>
      <div className="flex flex-wrap gap-2">{list.map((x) => (
        <button key={x.key} type="button" onClick={() => setK(x.key)} data-testid={`email-tab-${x.key}`}
          className={`rounded-full border px-3 py-1 text-xs font-semibold ${x.key === k ? "border-emerald-500 bg-emerald-500/10 text-emerald-300" : "border-slate-700 text-slate-300 hover:bg-slate-800"}`}>
          {x.label}{(x.custom || x.copy_admin) && <span className="ml-1 text-emerald-400">•</span>}</button>))}</div>
      <Template key={t.key} t={t} onSaved={onSaved} />
    </div>
  );
}

export function EmailSettings() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(P).then((r) => setD(r.data)).catch((e) => toast.error(formatErr(e))); }, []);
  if (!d) return <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />;
  const test = async () => { setBusy(true); try { const r = (await api.post("/asv/admin/settings/resend/test")).data; toast.success(`Test email sent to ${r.to}`); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); } };
  const groups = [...new Set(d.templates.map((t) => t.group))];
  return (
    <div className="max-w-5xl space-y-6" data-testid="email-settings">
      <AdminHeader title="Email settings" sub="Sender details and the content of every email the site sends." />
      <Sender d={d} onSaved={setD} onTest={test} busy={busy} />
      {groups.map((g) => <Group key={g} name={g} list={d.templates.filter((t) => t.group === g)} onSaved={setD} />)}
    </div>
  );
}
